import { Container, IDestroyOptions, Sprite, Text, TextStyle, utils } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { PaymentProcessingType } from "../type/PaymentProcessingType";
import { gsap } from "gsap";
import { GameFont } from "../../../common/GameFont";
import { IPaymentProcessingController } from "../controllers/IPaymentProcessingController";
import { PaymentProcessingPopupSceneOptions } from "../scenes/PaymentProcessingPopupSceneOptions";
import { Erc20PaymentProcessingResourcePackage } from "../resources/Erc20PaymentProcessingResourcePackage";
import { PaymentResult } from "../../../../../web3/PaymentResult";
import { Web3Model } from "../../../../../web3/models/Web3Model";
import { CancellationToken } from "../../../../../shared/async/CancellationToken";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { CancellationTokenSource } from "async/CancellationTokenSource";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { DefaultMessagesSceneFactory } from "../../../SceneFactories";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { IApplePayApi } from "by/gamefactory/cityversetycoon/purchases/IApplePayApi";

export class SelectPaymentMethodPopupView extends BaseScalableView<PaymentResult> {
    private static readonly CHOOSE_PAYMENT_METHOD_TEXT = "PLEASE SELECT PAYMENT METHOD OPTION";
    private static readonly USDC_PAYMENT_METHOD_TEXT = "USDC FROM\nIN-GAME WALLET";
    private static readonly EXTERNAL_PAYMENT_METHOD_TEXT = "CC/EXTERNAL\nCRYPTO WALLET";
    private static readonly SELECT_BTN_TEXT = "SELECT";

    private readonly sceneManager: SceneManager;
    private readonly contextResolver: IContextResolver;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly animations: gsap.core.Timeline;

    private fadeScreen: FadeScreen;
    private popupContainer: Container;
    private background: Sprite;
    private inGamePaymentBtn: ButtonWithScaleAnimation;
    private externaPaymentBtn: ButtonWithScaleAnimation;
    private header: Text;
    private closeButton: ButtonWithScaleAnimation;

    public constructor(
        operationControls: PromiseCompletionSource<PaymentResult>,
        canvasScaler: ICanvasScaler,
        private readonly commonResources: CommonResourcesPackage,
        private readonly erc20Resources: Erc20PaymentProcessingResourcePackage,
        private readonly paymentController: IPaymentProcessingController,
        private readonly options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>,
        private readonly web3Model: Web3Model,
        asyncOperationNotification: AsyncOperationNotification,
        sceneManager: SceneManager,
        contextResolver: IContextResolver,
        applePayApi: IApplePayApi,
        private readonly cancellationToken: CancellationToken,
        private readonly logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.asyncOperationNotification = asyncOperationNotification;
        this.contextResolver = contextResolver;
        this.sceneManager = sceneManager;
        this.animations = new gsap.core.Timeline({ paused: true });
        const haveEventName = "Home screen user has CC on Apple pay";
        const notHaveEventName = "Home screen DOESN'T have CC on Apple pay";
        let isApplePayAvailable = false;
        try {
            isApplePayAvailable = applePayApi.applePayAvailableWithActiveCardEvent(haveEventName, notHaveEventName);
        } catch (e: unknown) {
            this.logger.error("Exception on checking apple pay availability: ", e);
        }
        const isGooglePayAvailable = utils.isMobile.android.device;
        this.build(isApplePayAvailable, isGooglePayAvailable);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private build(isApplePayAvailable: boolean, isGooglePayAvailable: boolean): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.7, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "dynamic";
        this.addChild(this.fadeScreen);

        this.popupContainer = new Container();
        this.addChild(this.popupContainer);

        this.background = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_payment_method_bg_png);
        this.background.eventMode = "static";
        this.background.position.set((this.fadeScreen.width - this.background.width) * 0.5, this.fadeScreen.height * 0.25);
        this.popupContainer.addChild(this.background);

        this.header = new Text(SelectPaymentMethodPopupView.CHOOSE_PAYMENT_METHOD_TEXT, new HeaderTextStyle());
        this.header.position.set((this.background.width - this.header.width) * 0.5, 60);

        const inGamePaymentContainer = new Container();
        const inAppWalletIcon = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_internal_wallet_png);
        inAppWalletIcon.position.set(115, 137);
        const inAppMethodText = new Text(SelectPaymentMethodPopupView.USDC_PAYMENT_METHOD_TEXT, new PaymenMethodTextStyle());
        inAppMethodText.position.set(93, 296);

        this.inGamePaymentBtn = new ButtonWithScaleAnimation(this.erc20Resources.assets_views_erc_20_payment_processing_select_btn_png);
        this.inGamePaymentBtn.position.set(100, 432);
        const selectInAppMethodText = new Text(SelectPaymentMethodPopupView.SELECT_BTN_TEXT, new ButtonTextStyle());
        selectInAppMethodText.position.set((this.inGamePaymentBtn.width - selectInAppMethodText.width) * 0.5, 25);
        this.inGamePaymentBtn.addChild(selectInAppMethodText);
        this.inGamePaymentBtn.onUp.add(this.onInGameBuyButtonClickHandle, this);
        inGamePaymentContainer.addChild(inAppWalletIcon, inAppMethodText, this.inGamePaymentBtn);

        const externalPaymentContainer = new Container();
        const creditCard = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_cc_png);
        const externalWallet = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_exteral_wallet_png);

        const externalMethodText = new Text(SelectPaymentMethodPopupView.EXTERNAL_PAYMENT_METHOD_TEXT, new PaymenMethodTextStyle());
        externalMethodText.position.set(488, 296);

        this.externaPaymentBtn = new ButtonWithScaleAnimation(this.erc20Resources.assets_views_erc_20_payment_processing_select_btn_png);
        this.externaPaymentBtn.position.set(490, 432);
        const selectExternalMethodText = new Text(SelectPaymentMethodPopupView.SELECT_BTN_TEXT, new ButtonTextStyle());
        selectExternalMethodText.position.set((this.externaPaymentBtn.width - selectExternalMethodText.width) * 0.5, 25);
        this.externaPaymentBtn.addChild(selectExternalMethodText);
        this.externaPaymentBtn.onUp.add(this.onExternalBuyButtonClickHandle, this);

        if (isApplePayAvailable) {
            const applePayIcon = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_apple_pay_png);
            applePayIcon.position.set(425, 185);
            creditCard.position.set(551, 185);
            externalWallet.position.set(675, 151);
            externalPaymentContainer.addChild(applePayIcon, creditCard, externalWallet, externalMethodText, this.externaPaymentBtn);
        } else if (isGooglePayAvailable){ 
            const googlePayIcon = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_google_pay_png);
            googlePayIcon.position.set(425, 185);
            creditCard.position.set(551, 185);
            externalWallet.position.set(675, 151);
            externalPaymentContainer.addChild(googlePayIcon, creditCard, externalWallet, externalMethodText, this.externaPaymentBtn);
        } else {
            creditCard.position.set(481, 185);
            externalWallet.position.set(605, 151);
            externalPaymentContainer.addChild(creditCard, externalWallet, externalMethodText, this.externaPaymentBtn);
        }        

        const divider = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_divider_png);
        divider.position.set(388, 137);

        this.background.addChild(this.header, inGamePaymentContainer, divider, externalPaymentContainer);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.position.set(380, this.fadeScreen.height * 0.25 + this.background.height + 50);
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
        this.addChild(this.closeButton);
    }

    private async onCloseButtonClickHandle(): Promise<void> {
        this.closeButton.eventMode = "none";
        this.fadeScreen.eventMode = "none";
        await this.hide();
        this.operationControls.complete(PaymentResult.CANCELLED);
    }

    private async onInGameBuyButtonClickHandle(): Promise<PaymentResult> {
        const paymerResult = await this.paymentController.showPaymentProcessingPopup(this.cancellationToken);
        if (paymerResult === PaymentResult.SUCCESS) {
            await this.hide();
            this.operationControls.complete(paymerResult);
        }
         return paymerResult;
    }

    private async onExternalBuyButtonClickHandle(): Promise<PaymentResult> {
        const cancellationTokenSource = new CancellationTokenSource(this.cancellationToken);
        const token = cancellationTokenSource.token;
        try {
            let result: PaymentResult = null;
            if (this.options.type === PaymentProcessingType.STORE_PACKAGE) {
                const paymentOperation = this.web3Model.storePayment(this.options.tokenId, this.options.price, token);
                result = await this.showPaymentMessage(paymentOperation, token);
            } else {
                if (this.options.reservoirOrderId == null || this.options.reservoirOrderId === undefined) {
                    const paymentOperation = this.web3Model.paymentBuildingWithMint(this.options.buildingId, token, this.options.couponBenefitIds);
                    result = await this.showPaymentMessage(paymentOperation, token);
                } else {
                    const paymentOperation = this.web3Model.paymentBuildingWithoutMint(this.options.tokenId, token);
                    result = await this.showPaymentMessage(paymentOperation, token);
                }
            }
            if (result == PaymentResult.SUCCESS) {
                this.operationControls.complete(result);
            }
        } catch (e: unknown) {
            this.logger.error("Exception on store payment: ", e);
            return PaymentResult.FAILURE;
        } finally {
            cancellationTokenSource.dispose();
        }
    }

    private async showPaymentMessage(operation: Promise<PaymentResult>, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            this.asyncOperationNotification.show();
            const factory: DefaultMessagesSceneFactory = await this.contextResolver.resolve("DefaultMessagesSceneFactory");
            const scene = await factory({ operation, operationCancellationToken: cancellationToken, withProcessingPage: true });
            this.asyncOperationNotification.hide();
            if (cancellationToken.isCancellationRequested) {
                scene.view.destroy();
                return PaymentResult.CANCELLED;
            }
            return (await this.sceneManager.showViewableAsync(scene)) as PaymentResult;
        } catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error("Exception on showing payment message: ", e);
            return PaymentResult.FAILURE;
        }
    }

    private async show(): Promise<void> {
        this.animations.fromTo(this.popupContainer, { alpha: 0 }, { alpha: 1, duration: 0.2 });
        await Promise.all([this.animations.play(), this.fadeScreen.fadeIn()]);
    }

    private async hide(): Promise<void> {
        this.fadeScreen.fadeOut();
        await this.animations.reverse();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        this.animations.kill();
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: ["#01FFBE", "#26FF72"],
            fillGradientStops: [0.1],
            fontSize: 52,
            align: "center",
        });
    }
}

class PaymenMethodTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: "#ffffff",
            fontSize: 40,
            align: "center",
        });
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: "#ffffff",
            fontSize: 34,
            align: "center",
        });
    }
}
